import { cloneElement, forwardRef, isValidElement } from "react";
import styles from "./InputWrapper.module.scss";
import classNames from "classnames";
import IconButton from "../IconButton/IconButton";
import { Close } from "../Icon/Icon";
import Badge from "../Badge/Badge";

export type InputWrapperProps = {
  autofocus?: boolean;
  className?: string;
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
  disabled?: boolean;
  loading?: boolean;
  loadingMessage?: string;
  children: React.ReactElement<HTMLElement>;
  size?: "xsmall" | "small" | "medium" | "large";
  onClick?: () => void;
  unstyled?: boolean;
  error?: boolean;
  clearable?: boolean;
  onClear?: () => void;
};

const InputWrapper = forwardRef<HTMLDivElement, InputWrapperProps>(
  function InputWrapper(
    {
      className,
      leftSection,
      rightSection,
      disabled,
      children,
      size = "medium",
      unstyled,
      onClick,
      error,
      clearable,
      onClear,
      loading,
      loadingMessage = "Loading",
    },
    ref,
  ) {
    return (
      <div
        ref={ref}
        onClick={onClick}
        onKeyDown={onClick}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        className={classNames(styles.wrapper, className, {
          [styles.disabled]: disabled,
          [styles.clickable]: onClick,
        })}
      >
        {loading && (
          <Badge className={styles.loading} color="purple" loading>
            {loadingMessage}
          </Badge>
        )}
        {leftSection && <div className={styles.leftSection}>{leftSection}</div>}
        {isValidElement(children)
          ? cloneElement(children, {
              ...children.props,
              className: classNames(
                styles[size],
                {
                  [styles.unstyled]: unstyled,
                  [styles.input]: !unstyled,
                  [styles.iconLeft]: leftSection,
                  [styles.iconRight]: rightSection,
                  [styles.error]: error,
                },

                children?.props.className,
              ),
            })
          : children}
        {(rightSection || clearable) && (
          <div className={styles.rightSection}>
            {rightSection}
            {clearable && (
              <IconButton
                IconComponent={Close}
                className={styles.clear}
                aria-label="Clear"
                onClick={(e) => {
                  e.stopPropagation();
                  onClear?.();
                }}
              />
            )}
          </div>
        )}
      </div>
    );
  },
);

export default InputWrapper;
